import { useState } from "react";
import { Copy, Check, Sparkles } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import type { AiNarrative as AiNarrativeType } from "@/types/api";

interface AiNarrativeProps {
  narrative: AiNarrativeType | null;
  isLoading?: boolean;
}

export function AiNarrative({ narrative, isLoading }: AiNarrativeProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!narrative) return;
    const text = [
      narrative.executive_summary,
      "",
      "Key Highlights:",
      ...narrative.key_highlights.map((h) => `- ${h}`),
      "",
      "Recommendations:",
      ...narrative.recommendations.map((r) => `- ${r}`),
    ].join("\n");

    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          <CardTitle className="text-base">Executive Summary</CardTitle>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCopy}
          disabled={!narrative}
          className="h-8 gap-1.5"
        >
          {copied ? (
            <><Check className="h-3.5 w-3.5" /> Copied</>
          ) : (
            <><Copy className="h-3.5 w-3.5" /> Copy Summary</>
          )}
        </Button>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            <div className="h-4 w-full animate-pulse rounded bg-muted" />
            <div className="h-4 w-5/6 animate-pulse rounded bg-muted" />
            <div className="h-4 w-4/6 animate-pulse rounded bg-muted" />
          </div>
        ) : narrative ? (
          <div className="space-y-4">
            <div className="prose prose-sm prose-invert max-w-none whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">
              {narrative.executive_summary}
            </div>
            {narrative.key_highlights.length > 0 && (
              <div>
                <h4 className="mb-2 text-sm font-semibold">Key Highlights</h4>
                <ul className="space-y-1">
                  {narrative.key_highlights.map((h, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                      {h}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {narrative.recommendations.length > 0 && (
              <div>
                <h4 className="mb-2 text-sm font-semibold">Recommendations</h4>
                <ul className="space-y-1">
                  {narrative.recommendations.map((r, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-orange-400" />
                      {r}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            AI summary unavailable. Metrics are still displayed above.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
